import { lazy, Suspense, useState, useMemo } from 'react';
import { ArrowForwardRounded } from '@mui/icons-material';
import CardActionArea from '@mui/material/CardActionArea';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import ResponsiveSurface from '../ResponsiveSurface';

import useInterviewType from 'src/interviews/hooks/useInterviewType';
import useResult from 'src/interviews/hooks/useResults';
import useInterview from 'src/interviews/hooks/useInterview';
import useImpressionTracking from 'src/hooks/useImpressionTracking';

import RelatedQuestions from '../Completion/components/RelatedQuestions';
import RelatedReading from '../Completion/components/RelatedReading';

import {
  trackMedicationAssessmentButtonViewed,
  trackMedicationAssessmentButtonClicked,
} from './analytics';

const MedicationAssessmentBody = lazy(() => import('./components/Body'));

const MedicationAssessment = () => {
  const interviewType = useInterviewType();
  const interview = useInterview();

  const [showMedicationAssessment, setShowMedicationAssessment] = useState(false);

  const analyticsProps = useMemo(
    () => ({
      interviewToken: interview.token || '',
      interviewType,
    }),
    [interviewType, interview.token],
  );

  const impressionRef = useImpressionTracking<HTMLButtonElement>(() => {
    trackMedicationAssessmentButtonViewed(analyticsProps);
  });

  const handleViewTreatmentPlanClick = () => {
    setShowMedicationAssessment(true);
    trackMedicationAssessmentButtonClicked(analyticsProps);
  };

  const closeMedicationAssessment = () => {
    setShowMedicationAssessment(false);
  };

  const result = useResult();
  const medications = result?.submitted_medications || [];

  return (
    <>
      <CardActionArea
        onClick={handleViewTreatmentPlanClick}
        className="bp-flex bp-justify-between bp-rounded-none bp-bg-gray-10 bp-px-3 bp-py-2"
        ref={impressionRef}
      >
        <Typography variant="body2" className="bp-text-xs">
          View full medication assessment
        </Typography>
        <ArrowForwardRounded fontSize="small" />
      </CardActionArea>

      <ResponsiveSurface
        isOpen={showMedicationAssessment}
        onClose={closeMedicationAssessment}
        modalHeader="Medication Assessment"
        backText="Back to results"
      >
        <Grid container spacing={2} direction="column" padding={2} paddingTop={1}>
          <Suspense fallback={null}>
            <MedicationAssessmentBody
              goBack={closeMedicationAssessment}
              interviewType={interviewType}
              medications={medications}
              result={result}
            />
            {result && <RelatedQuestions interviewType={interviewType} result={result} />}
            {result && <RelatedReading interviewType={interviewType} result={result} />}
          </Suspense>
        </Grid>
      </ResponsiveSurface>
    </>
  );
};

export default MedicationAssessment;
